import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Award, Star } from "lucide-react";
import AchievementBadge from "@/components/badges/AchievementBadge";
import { BADGE_CATEGORIES } from "@/lib/badges";
import CoachStudentContributions from "@/components/badges/CoachStudentContributions";

export default function CoachStudentBadges({ studentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [awarding, setAwarding] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/badges/student/${studentId}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [studentId]);

  const awardCoachPick = async () => {
    setAwarding(true);
    try {
      await api.post(`/badges/student/${studentId}/award`, { badge_code: "coach_pick" });
      toast.success("Badge « Coup de cœur coach » attribué");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'attribuer le badge");
    } finally {
      setAwarding(false);
    }
  };

  if (loading) return <p className="text-slate-400 text-center py-8">Chargement…</p>;

  const earnedCodes = new Set((data?.earned || []).map((b) => b.badge_code));
  const earnedMap = Object.fromEntries((data?.earned || []).map((b) => [b.badge_code, b]));
  const hasCoachPick = earnedCodes.has("coach_pick");

  const byCategory = {};
  (data?.definitions || []).forEach((def) => {
    const cat = def.category || "special";
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(def);
  });

  return (
    <div className="space-y-6">
      <Card className="border-amber-100 bg-amber-50/40">
        <CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Award className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">{data?.stats?.earned_count || 0} badges débloqués</p>
              <p className="text-sm text-slate-500">Motivation et régularité de l'élève</p>
            </div>
          </div>
          <Button
            onClick={awardCoachPick}
            disabled={awarding || hasCoachPick}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-full gap-2"
          >
            <Star className="w-4 h-4" />
            {hasCoachPick ? "Coup de cœur déjà attribué" : "Attribuer Coup de cœur"}
          </Button>
        </CardContent>
      </Card>

      <CoachStudentContributions studentId={studentId} />

      {Object.entries(BADGE_CATEGORIES).map(([key, meta]) => {
        const items = byCategory[key];
        if (!items?.length) return null;
        return (
          <section key={key}>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{meta.label}</h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
              {items.map((def) => (
                <AchievementBadge
                  key={def.code}
                  title={def.title}
                  description={def.description}
                  icon={def.icon}
                  category={def.category}
                  earned={earnedCodes.has(def.code)}
                  isNew={earnedMap[def.code]?.is_new}
                  progress={data?.progress?.[def.code]}
                  size="md"
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}